import { useEffect, useMemo, useRef } from "react";
import styles from "./index.module.scss";
import cn from "classnames";
import MinersTab from "./MinersTab";
import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import AddTab from "./AddTab";
import { useMe } from "@utils/hooks/useMe";
import { show } from "redux-modal";
import { MODALS_ENUM } from "@constants/Modals";
import { AddTabModalPropsI } from "./AddTabModal";
import { useDispatch, useSelector } from "react-redux";
import { ConfirmModalExposedPropsI } from "@components/modals/newModals/ConfirmModal/ConfirmModal";
import { useIntl } from "react-intl";
import { Tab } from "@store/minersSelect/actionTypes";
import { RootState } from "@store";
import { setTab } from "@store/minersSelect/actions";
import useMediaQuery from "@utils/hooks/utils";
import useClickOutside from "@utils/hooks/useClickOutside";
import useTypedRequest from "@utils/hooks/useTypedRequest";
import {
  createTabRequest,
  deleteTabRequest,
  listTabsRequest,
  renameTabRequest,
} from "@services/vtabs";
import { fromEntries } from "../../../utils/entries";
import dbg from "@utils/dbg";

const CONFIG_CAN_ADD_TABS = true;

export interface MinerTableTabsPropsI {
  className?: string;
}

export type DragSources = Partial<Record<Tab, DragType>>;

export interface TabDescriptor {
  name: string;
  id: number;
  hasItems?: boolean;
  title?: string;
  adminOnly?: boolean;
  dragSource?: boolean;
  dragTarget?: DragSources;
}

export enum DragType {
  MOVE, // нужно сначала вызвать удаление, потом копирование
  COPY, // нужно вызвать только копирование
  MOVECALL, // однократный вызов API приводит сразу к переносу или копированию майнера, пользовательский выбор не нужен
  USER, // нужен пользовательский выбор
}

export const DefaultDragSources: DragSources = {
  [Tab.ALL]: DragType.COPY,
  [Tab.ACTIVE]: DragType.COPY,
  [Tab.INCORRECTID]: DragType.COPY,
  [Tab.OFFLINE]: DragType.COPY,
  [Tab.PROBLEM]: DragType.COPY,
  [Tab.RACKS]: DragType.COPY,
  [Tab.SERVICE]: DragType.USER,
  [Tab.STORE]: DragType.USER,
  [Tab.RETIRED]: DragType.USER,
} as const;

const SpecialTabs: TabDescriptor[] = [
  {
    id: Tab.ALL,
    name: "machines.table.tabs.all_tab",
    title: "machines.table.tabs.all_tab.title",
    dragSource: true,
  }, //
  {
    id: Tab.RACKS,
    name: "machines.table.tabs.racks_tab",
    title: "machines.table.tabs.racks_tab.title",
    adminOnly: true,
    dragSource: true,
  }, //
  {
    id: Tab.ACTIVE,
    name: "machines.table.tabs.active_tab",
    title: "machines.table.tabs.active_tab.title",
    dragSource: true,
  },
  {
    id: Tab.PROBLEM,
    name: "machines.table.tabs.problem_tab",
    title: "machines.table.tabs.problem_tab.title",
    dragSource: true,
  }, //
  {
    id: Tab.OFFLINE,
    name: "machines.table.tabs.offline_tab",
    title: "machines.table.tabs.offline_tab.title",
    dragSource: true,
  }, //
  {
    id: Tab.SERVICE,
    name: "machines.table.tabs.service_tab",
    title: "machines.table.tabs.service_tab.title",
    dragSource: true,
    dragTarget: DefaultDragSources,
  }, //
  {
    id: Tab.STORE,
    name: "machines.table.tabs.store_tab",
    title: "machines.table.tabs.store_tab.title",
    dragSource: true,
    dragTarget: DefaultDragSources,
  }, //
  {
    id: Tab.RETIRED,
    name: "machines.table.tabs.retired_tab",
    title: "machines.table.tabs.retired_tab.title",
    adminOnly: true,
    dragSource: true,
    dragTarget: DefaultDragSources,
  }, //
  {
    id: Tab.INCORRECTID,
    name: "machines.table.tabs.incorrectid_tab",
    title: "machines.table.tabs.incorrectid_tab.title",
    adminOnly: true,
    dragSource: true,
  },
  {
    id: Tab.UNKNOWN,
    name: "machines.table.tabs.unknown_tab",
    title: "machines.table.tabs.unknown_tab",
    adminOnly: true,
  },
];

export const DragSources: ReadonlySet<Tab> = new Set(
  SpecialTabs.filter((t) => t.dragSource).map((t) => t.id)
);
export const DragTargets: Record<Tab, DragSources> = fromEntries<
  Tab,
  DragSources
>(SpecialTabs.filter((t) => t.dragTarget).map((t) => [t.id, t.dragTarget!]));
const LastTabs = (isAdmin: boolean) => isAdmin ? new Set([Tab.RETIRED, Tab.UNKNOWN]) : new Set([Tab.STORE]);

const useControl = (props: MinerTableTabsPropsI) => {
  const curID = useSelector((root: RootState) => root.minersSelect.tab);
  const [tabs, setTabs] = useState<TabDescriptor[]>([]);
  const [hideOthers, setHideOthers] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const me = useMe();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("max", 768);
  const selectorRef = useRef<HTMLDivElement | null>(null);
  const { formatMessage } = useIntl();
  const scrollableRef = useRef<HTMLDivElement | null>(null);
  const lastScrollLeft = useRef(0);
  const isDrag = useSelector(
    (root: RootState) => !!root.minersSelect.dragMinersIDs?.length
  );

  const { request: listTabs, loading: loadingTabs } =
    useTypedRequest(listTabsRequest);
  const { request: createTab } = useTypedRequest(createTabRequest);
  const { request: deleteTab } = useTypedRequest(deleteTabRequest);
  const { request: renameTab } = useTypedRequest(renameTabRequest);

  const tabName = (td: TabDescriptor): string =>
    td.id < 0 ? formatMessage({ id: td.name }) : td.name;
  const tabTitle = (td: TabDescriptor): string => {
    return td.id < 0 ? formatMessage({ id: td.title }) : td.title || td.name;
  };

  useEffect(() => {
    if (!me?.is_admin) return;
    (async () => {
      const r = await listTabs();
      if (!r) return;
      setTabs(r.Records);
    })();
  }, [me?.is_admin]);

  useClickOutside(() => {
    setTimeout(() => setOpen(false), 500);
  }, selectorRef.current || undefined);

  const onNewTab = async (name: string) => {
    const oldTabs = [...tabs];
    setTabs((t) => [...t, { name, id: -1000 }]);
    const r = await createTab(name);
    if (!r) {
      setTabs([...oldTabs]);
      return;
    }
    setTabs([...oldTabs, { name, id: r.Records[0] }]);
  };

  const setCurID = (id: number) => {
    //console.log("SET CUR TAB", id);
    dispatch(setTab(id));
  };

  const modalProps: AddTabModalPropsI = {
    submit: onNewTab,
  };
  const isAdmin = me?.is_admin;
  const allSpecTabs = useMemo(
    () => SpecialTabs.filter((desc) => isAdmin || !desc.adminOnly),
    [isAdmin, curID]
  );
  const curTab = useMemo(() => {
    let t = allSpecTabs.find((desc) => desc.id === curID);
    if (t !== undefined) return t;
    return tabs.find((desc) => desc.id === curID);
  }, [curID, tabs, allSpecTabs]);
  const specTabs = useMemo(
    () =>
      SpecialTabs.filter(
        (desc) =>
          (isAdmin || !desc.adminOnly) && !(isMobile && desc.id === curID)
      ),
    [isMobile, isAdmin, curID]
  );

  useEffect(() => {
    const cb = (ev: MouseEvent) => {
      if (!scrollableRef.current) return;
      const rect = scrollableRef.current.getBoundingClientRect();
      if (ev.clientY >= rect.top && ev.clientY <= rect.bottom) {
        const now = new Date().getTime();
        if (now - lastScrollLeft.current < 100) return;
        if (ev.clientX < rect.left) scrollableRef.current.scrollBy(-20, 0);
        else if (ev.clientX > rect.right) scrollableRef.current.scrollBy(20, 0);
        lastScrollLeft.current = now;
      }
    };
    if (isDrag) {
      window.addEventListener("mousemove", cb);
      return () => window.removeEventListener("mousemove", cb);
    }
  }, [isDrag]);

  return {
    tabs,
    isAdmin,
    isOpen,
    curID,
    hideOthers,
    specTabs,
    curTab,
    isMobile,
    selectorRef,
    scrollableRef,

    hide() {
      setOpen(false);
    },
    toggleOpen() {
      setOpen((x) => !x);
    },
    setCurID,
    showAddTabModal() {
      dispatch(show(MODALS_ENUM.ADD_MINERS_TABLE_TAB, modalProps));
    },
    deleteTab(id: number) {
      const mprops: ConfirmModalExposedPropsI = {
        async onConfirm() {
          const oldTabs = [...tabs];
          setTabs((tabs) => tabs.filter((t) => t.id !== id));
          const r = await deleteTab(id);
          if (!r) setTabs(oldTabs);
        },
      };
      dispatch(show(MODALS_ENUM.CONFIRM_MODAL, mprops));
    },
    async renameTab(id: number, newName: string) {
      const oldTabs = [...tabs];
      setTabs((tabs) =>
        tabs.map((t) => (t.id === id ? { ...t, name: newName } : t))
      );
      const r = await renameTab(id, newName);
      if (!r) setTabs(oldTabs);
    },
    setHideOthers,
    tabName,
    tabTitle,
  };
};

export default function MinersTableTabs(props: MinerTableTabsPropsI) {
  const ctrl = useControl(props);
  return (
    <div className={styles.tabs_container}>
      <div className={styles.outer}>
      <div className={styles.settings_wrapper}>
        <div className={cn(styles.dropdown)} ref={ctrl.selectorRef}>
          {!!ctrl.curTab && (
            <MinersTab
              setTab={() => ctrl.setCurID(ctrl.curTab!.id)}
              thisID={ctrl.curTab!.id}
              currentID={ctrl.curID}
              tabName={ctrl.tabName(ctrl.curTab!)}
              title={ctrl.tabTitle(ctrl.curTab!)}
              key={ctrl.curTab!.id}
              special={ctrl.curID < 0}
              onClick={ctrl.toggleOpen}
            />
          )}
          <div className={cn(styles.opener, { [styles.open]: ctrl.isOpen })} />
        </div>
        <div
          className={cn(styles.btns_wrapper, { [styles.hidden]: !ctrl.isOpen })}
          ref={ctrl.scrollableRef}
        >
          <AnimatePresence initial={false}>
            {ctrl.specTabs.map((desc, i) => (
              <MinersTab
                setTab={() => ctrl.setCurID(desc.id)}
                thisID={desc.id}
                currentID={ctrl.curID}
                tabName={ctrl.tabName(desc)}
                title={ctrl.tabTitle(desc)}
                key={desc.id}
                special
                last={LastTabs(!!ctrl.isAdmin).has(desc.id)}
                skippable
              />
            ))}
            {!ctrl.isMobile && <div style={{ minWidth: "21px" }} />}
            {CONFIG_CAN_ADD_TABS &&
              ctrl.isAdmin &&
              ctrl.tabs.map((desc, i) => (
                <MinersTab
                  setTab={() => ctrl.setCurID(desc.id)}
                  thisID={desc.id}
                  currentID={ctrl.curID}
                  tabName={ctrl.tabName(desc)}
                  key={desc.id}
                  deleteTab={ctrl.deleteTab}
                  renameTab={ctrl.renameTab}
                  hasItems={desc.hasItems}
                  last={i === ctrl.tabs.length - 1}
                  skippable
                />
              ))}
          </AnimatePresence>
        </div>
      </div>
      </div>
      {CONFIG_CAN_ADD_TABS && ctrl.isAdmin && (
        <AddTab onClick={() => ctrl.showAddTabModal()} />
      )}
    </div>
  );
}
